import {
  KycReviewDocumentStatus,
  KycReviewDocumentType,
} from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import { AnyState } from 'common/bento/types/Abstract';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';

import OBFileItem from '../../../../components/OBFileItem';
import { State } from '../../machine/machine';
import { AddressModuleData } from '../../machine/model';
import ReviewAddressPage from '../ReviewAddress';
import { ReviewAddressProps } from '../ReviewAddress/ReviewAddress';
import locales from './locales';

interface ReviewAddressAndPoaProps extends ReviewAddressProps {
  isSelfHosted: boolean;
  isLoading: boolean;
  moduleData: AddressModuleData;
  state: AnyState;
  onDeleteClick: (documentId: string) => void;
  onContinue: () => void;
}

/**
 * A page that asks the user to confirm their address
 * and their POA or their host's
 */
const ReviewAddressAndPoa = asBentoPage<ReviewAddressAndPoaProps>((props) => {
  const {
    isLoading,
    isSelfHosted,
    moduleData,
    onContinue,
    onDeleteClick,
    state,
  } = props;

  const kycDocuments = moduleData.viewer.companyUser.kycDocuments.filter(
    ({ status }) => status !== KycReviewDocumentStatus.Refused,
  );

  const poaDocumentType = isSelfHosted
    ? KycReviewDocumentType.ProofOfAddress
    : KycReviewDocumentType.ProofOfAddressHost;

  const proofOfAddressDocument = kycDocuments.find(
    (doc) => doc?.type === poaDocumentType,
  );

  if (!proofOfAddressDocument) {
    if ([State.POA_REVIEW, State.HOSTED_POA_REVIEW].some(state.matches)) {
      throw new Error('Proof of address should be defined');
    }

    // When state matches DELETE_POA or HOSTED_POA_DELETE
    // and the cache has just been updated after the deletion
    // kycDocuments will be empty just before transitioning to the next state
    // and thus proofOfAddressDocument will be undefined
    return null;
  }

  const { kycDocumentId, url } = proofOfAddressDocument;

  const handleViewDocumentClick = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <ReviewAddressPage {...props} data-testid="poa-review">
      <Text
        fontWeight="weight-500"
        marginBottom="space-8"
        size="lg"
        variant="primary"
      >
        2. {locales.subTitleCheckProofOfAddress}
      </Text>

      <OBFileItem
        fileName={locales.proofOfAddressFileLabel}
        isLoading={isLoading}
        onDeleteClick={() => onDeleteClick(kycDocumentId)}
        onPreviewClick={handleViewDocumentClick}
      />

      <Button
        isDisabled={isLoading}
        isExpanded
        marginTop="space-48"
        onClick={onContinue}
        type="submit"
      >
        {locales.next}
      </Button>
    </ReviewAddressPage>
  );
});

export default ReviewAddressAndPoa;
